/**
 * Structured logging utility
 *
 * Provides debug, info, warn, and error logging with optional context.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Logger instance
 */
export interface ILogger {
  debug(message: string, context?: Record<string, unknown> | Error): void;
  info(message: string, context?: Record<string, unknown> | Error): void;
  warn(message: string, context?: Record<string, unknown> | Error): void;
  error(message: string, error?: Error | Record<string, unknown>): void;
  setLevel(level: LogLevel): void;
}

/**
 * Internal logger implementation
 */
class Logger implements ILogger {
  private level: LogLevel = 'info';

  private levelValue = { debug: 0, info: 1, warn: 2, error: 3 };

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  private shouldLog(level: LogLevel): boolean {
    return this.levelValue[level] >= this.levelValue[this.level];
  }

  private formatMessage(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown> | Error,
  ): string {
    const timestamp = new Date().toISOString();
    const levelUpper = level.toUpperCase().padEnd(5);
    let contextStr = '';

    if (context) {
      if (context instanceof Error) {
        contextStr = ` ${context.message}`;
      } else if (Object.keys(context).length > 0) {
        contextStr = ` ${JSON.stringify(context)}`;
      }
    }

    return `[${timestamp}] ${levelUpper} ${message}${contextStr}`;
  }

  debug(message: string, context?: Record<string, unknown> | Error): void {
    if (this.shouldLog('debug')) {
      console.debug(this.formatMessage('debug', message, context));
    }
  }

  info(message: string, context?: Record<string, unknown> | Error): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage('info', message, context));
    }
  }

  warn(message: string, context?: Record<string, unknown> | Error): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message, context));
    }
  }

  error(message: string, error?: Error | Record<string, unknown>): void {
    if (this.shouldLog('error')) {
      let errorContext: Record<string, unknown> = {};
      if (error instanceof Error) {
        errorContext = {
          errorName: error.name,
          errorMessage: error.message,
          errorStack: error.stack,
        };
      } else if (error) {
        errorContext = error;
      }
      console.error(this.formatMessage('error', message, errorContext));
    }
  }
}

/**
 * Global logger instance
 */
let globalLogger: ILogger | null = null;

/**
 * Get or create global logger
 * @param level - Initial log level
 * @returns Logger instance
 */
export function getLogger(level: LogLevel = 'info'): ILogger {
  if (!globalLogger) {
    globalLogger = new Logger();
    globalLogger.setLevel(level);
  }
  return globalLogger;
}

/**
 * Set global log level
 * @param level - New log level
 */
export function setLogLevel(level: LogLevel): void {
  getLogger().setLevel(level);
}

/**
 * Create a named logger for a module
 * @param moduleName - Module name for context
 * @returns Logger with module name in context
 */
export function createModuleLogger(moduleName: string): ILogger {
  const baseLogger = getLogger();
  return {
    debug: (msg, ctx) => baseLogger.debug(`[${moduleName}] ${msg}`, ctx as Record<string, unknown> | Error),
    info: (msg, ctx) => baseLogger.info(`[${moduleName}] ${msg}`, ctx as Record<string, unknown> | Error),
    warn: (msg, ctx) => baseLogger.warn(`[${moduleName}] ${msg}`, ctx as Record<string, unknown> | Error),
    error: (msg, err) => baseLogger.error(`[${moduleName}] ${msg}`, err),
    setLevel: (level) => baseLogger.setLevel(level),
  };
}
